import { Component, computed, inject, input, LOCALE_ID, OnInit, output, signal } from '@angular/core';
import { CommonModule } from '@angular/common';
import { CustomerProductListItem } from '../../../../../pages/user-dashboard/user-products/customer-product.model';
import { Router } from '@angular/router';
import { ProductsFileMgt } from '../../../../../services/product/file-management/products.file-mgt.service';
import { SafeUrl } from '@angular/platform-browser';
import {
  OneBundleTypeDto,
  OneOfferTypeDto,
} from '../../../../../pages/user-dashboard/user-offer-details/user-offer-details.model';
import { AddWishlistIcon } from '../../../../../shared/ui/icons/product-card/add-wishlist-icon/add-wishlist-icon';
import { AddCartIcon } from '../../../../../shared/ui/icons/product-card/add-cart-icon/add-cart-icon';
import { BusinessPriceIcon } from '../../../../../shared/ui/icons/product-card/business-price-icon/business-price-icon';
import { CustomerPriceIcon } from '../../../../../shared/ui/icons/product-card/customer-price-icon/customer-price-icon';
import { MfgExpDatesIcon } from '../../../../../shared/ui/icons/product-card/mfg-exp-dates-icon/mfg-exp-dates-icon';
import { BatchNumberIcon } from '../../../../../shared/ui/icons/product-card/batch-number-icon/batch-number-icon';
import { RemainingQuantityIcon } from '../../../../../shared/ui/icons/product-card/remaining-quantity-icon/remaining-quantity-icon';
import { LimitReachedIcon } from '../../../../../shared/ui/icons/product-card/limit-reached-icon/limit-reached-icon';
import { PharmacyCreditService } from '../../../../../services/pharmacy-credit/pharmacy-credit';

type CardProduct =
  CustomerProductListItem | OneBundleTypeDto['products'][number]['product'] | OneOfferTypeDto['products'][number];
@Component({
  selector: 'app-product-card-component',
  standalone: true,
  imports: [
    CommonModule,
    AddWishlistIcon,
    AddCartIcon,
    BusinessPriceIcon,
    CustomerPriceIcon,
    MfgExpDatesIcon,
    BatchNumberIcon,
    RemainingQuantityIcon,
    LimitReachedIcon,
  ],
  templateUrl: './product-card-component.html',
})
export class ProductCardComponent implements OnInit {
  product = input.required<CardProduct>();
  variant = input<'list' | 'offer' | 'bundle'>('list');
  hideCompany = input(false);
  bundleQuantity = input<number | null>(null);
  addToWishlist = output<CustomerProductListItem>();
  activePopup = output<{
    productId: string;
    quantity: string;
    unitPrice: number;
    isOffer: boolean;
    offerId: string;
    remainingQuantity: number | null;
  }>();
  quantity = signal('');

  private router = inject(Router);
  private localeId = inject(LOCALE_ID);
  private fileMgt = inject(ProductsFileMgt);
  private pharmacyCreditService = inject(PharmacyCreditService);

  isArabic = computed(() => this.localeId.startsWith('ar'));
  isOffer = computed(() => this.variant() === 'offer');
  isPharmacySuspended = computed(() => this.pharmacyCreditService.isSelectedPharmacySuspended());
  isOutOfStock = computed(() => {
    const p = this.product();

    if ('isOutOfStock' in p) {
      return p.isOutOfStock || p.quantity === 0;
    }

    return false;
  });
  imagePreview = signal<SafeUrl | string>('/assets/product.png');

  // Product order limit for the selected pharmacy (null = no limit configured)
  remainingQuantity = computed(() => {
    const p = this.product();

    return 'remainingQuantity' in p ? (p.remainingQuantity ?? null) : null;
  });
  isLimitReached = computed(() => {
    const remaining = this.remainingQuantity();

    return remaining != null && remaining <= 0;
  });

  offerDiscount = computed(() => {
    const p = this.product();

    return 'offerDiscount' in p ? p.offerDiscount : null;
  });

  // Out of Stock / Extra Discount tag already sits in the top corner of the image
  hasCornerTag = computed(() => this.isOutOfStock() || !!this.offerDiscount());

  mfgCompany = computed(() => {
    const p = this.product();

    return 'mfgCompany' in p ? p.mfgCompany : null;
  });
  businessPrice = computed(() => this.product().businessPrice);
  businessPriceDiscountPercentage = computed(() => {
    const p = this.product();

    return 'businessPriceDiscountPercentage' in p ? p.businessPriceDiscountPercentage : null;
  });

  ngOnInit() {
    const fid = this.product().profileImageFid;
    if (fid) {
      this.fileMgt.getImageUrl(fid).subscribe({
        next: (res) => {
          if (res.isImage) this.imagePreview.set(res.url);
        },
        error: () => this.imagePreview.set('/assets/product.png'),
      });
    }
  }

  navigateToDetails() {
    this.router.navigate(['/user/products/view-product', this.product().id]);
  }

  onActivePopup(e: Event) {
    e.stopPropagation();
    if (this.isPharmacySuspended() || this.isLimitReached()) return;

    const p = this.product();

    console.log('p: ', p);

    this.activePopup.emit({
      productId: p.id.toString(),
      quantity: this.quantity(),
      unitPrice: this.discountedBusinessPrice(),
      // isOffer: 'offerDiscount' in p && !!p.offerDiscount,
      isOffer: !!(p as any).offerBxGy,

      offerId: 'offerDiscount' in p && p.offerDiscount ? p.offerDiscount.id.toString() : '',
      remainingQuantity: this.remainingQuantity(),
    });
  }

  onAddToWishlist(e: Event) {
    e.stopPropagation();

    const p = this.product();

    if ('offerDiscount' in p) {
      this.addToWishlist.emit(p);
    }
  }

  discountedBusinessPrice(): number {
    const p = this.product();

    if (!('offerDiscount' in p) || !p.offerDiscount) {
      return +p.businessPrice;
    }

    const { discountType, discountValue } = p.offerDiscount;

    return discountType === 'PERCENTAGE'
      ? +(Number(p.businessPrice) - (Number(p.businessPrice) * discountValue) / 100).toFixed(2)
      : +(Number(p.businessPrice) - discountValue).toFixed(2);
  }
}
